import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  Progress,
  Text,
  VStack,
  useColorModeValue,
  Collapse,
  Slide,
  Spinner,
} from '@chakra-ui/react';
import { CloseIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import OnboardingStepContent from './OnboardingStepContent';

export default function OnboardingOverlay() {
  // All hooks must be called before any conditional returns
  const {
    isOnboardingActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    nextStep,
    previousStep,
    skipOnboarding,
    isMinimized,
    toggleMinimize,
    createdAgentId,
  } = useOnboarding();

  const db = getFirestore();

  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'navy.700');
  const progressColor = useColorModeValue('brand.500', 'brand.400');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const headerBg = useColorModeValue('gray.50', 'navy.900');
  const progressBg = useColorModeValue('gray.100', 'navy.700');
  const agentEmailBg = useColorModeValue('blue.50', 'blue.900');
  const statusActiveBg = useColorModeValue('green.50', 'green.900');
  const statusWaitingBg = useColorModeValue('orange.50', 'orange.900');

  // Dragging state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  // Agent status for activation step
  const [agentIsActive, setAgentIsActive] = useState(false);
  const [checkingAgentStatus, setCheckingAgentStatus] = useState(false);
  const [agentEmail, setAgentEmail] = useState('');

  const handleMouseDown = (e) => {
    // Only allow dragging from header, not from buttons
    if (e.target.closest('button')) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  // Add global mouse event listeners
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Check agent status when on activation step
  useEffect(() => {
    const checkAgentStatus = async () => {
      if (!currentStep?.waitForAgentActive || !createdAgentId) {
        return;
      }

      setCheckingAgentStatus(true);
      try {
        const agentRef = doc(db, 'agents', createdAgentId);
        const agentSnap = await getDoc(agentRef);
        
        if (agentSnap.exists()) {
          const agentData = agentSnap.data();
          // Agent is only ready when isReady is explicitly true
          const isActive = agentData.isReady === true;
          setAgentIsActive(isActive);
          setAgentEmail(agentData.email || '');
        }
      } catch (error) {
        console.error('Error checking agent status:', error);
      } finally {
        setCheckingAgentStatus(false);
      }
    };

    checkAgentStatus();

    // Poll every 30 seconds if on activation step and agent not active
    if (currentStep?.waitForAgentActive && !agentIsActive) {
      const interval = setInterval(checkAgentStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [currentStep, createdAgentId, db, agentIsActive]);

  if (!isOnboardingActive || !currentStep) {
    return null;
  }

  return (
    <Slide direction="bottom" in={isOnboardingActive} style={{ zIndex: 1400 }}>
      <Box
        ref={cardRef}
        position="fixed"
        bottom={{ base: 0, md: 4 }}
        right={{ base: 0, md: 4 }}
        w={{ base: '100%', md: '450px' }}
        maxW="calc(100vw - 32px)"
        maxH={{ base: '85vh', md: '90vh' }}
        zIndex={1400}
        transform={`translate(${position.x}px, ${position.y}px)`}
        transition={isDragging ? 'none' : 'transform 0.2s ease'}
        cursor={isDragging ? 'grabbing' : 'default'}
      >
        <Card
          bg={cardBg}
          boxShadow="2xl"
          borderRadius={{ base: '0', md: 'xl' }}
          border="1px solid"
          borderColor={borderColor}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          h="auto"
          maxH="inherit"
        >
          {/* Header - Draggable */}
          <Flex
            justify="space-between"
            align="center"
            p={4}
            borderBottom="1px solid"
            borderColor={borderColor}
            bg={headerBg}
            cursor="grab"
            onMouseDown={handleMouseDown}
            userSelect="none"
            _active={{ cursor: 'grabbing' }}
          >
            <Flex align="center" gap={2}>
              <Text fontSize="md" fontWeight="700" color={textColor}>
                Setup Guide
              </Text>
              <Text fontSize="xs" color={textColorSecondary}>
                Step {currentStepIndex + 1} of {totalSteps}
              </Text>
            </Flex>
            <Flex gap={1}>
              <IconButton
                aria-label={isMinimized ? 'Expand' : 'Minimize'}
                icon={isMinimized ? <ChevronUpIcon /> : <ChevronDownIcon />}
                size="sm"
                variant="ghost"
                onClick={toggleMinimize}
              />
              <IconButton
                aria-label="Close onboarding"
                icon={<CloseIcon />}
                size="sm"
                variant="ghost"
                onClick={skipOnboarding}
              />
            </Flex>
          </Flex>

          {/* Progress bar */}
          <Progress
            value={((currentStepIndex + 1) / totalSteps) * 100}
            size="xs"
            colorScheme="brand"
            bg={progressBg}
          />

          <Collapse in={!isMinimized} animateOpacity>
            {/* Content */}
            <Box
              overflowY="auto"
              overflowX="hidden"
              flex="1"
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: progressColor,
                  borderRadius: '24px',
                },
              }}
            >
              <VStack spacing={4} p={6} align="stretch">
                {/* Step Content */}
                <OnboardingStepContent
                  title={currentStep.title}
                  content={currentStep.content}
                />

                {/* Agent email display for activation step */}
                {currentStep.id === 'agent-activation' && agentEmail && (
                  <Box
                    p={3}
                    bg={agentEmailBg}
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderColor="brand.500"
                  >
                    <Text fontSize="sm" fontWeight="bold" color={textColor} mb={1}>
                      Agent Email:
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      {agentEmail}
                    </Text>
                  </Box>
                )}

                {/* Agent status for activation step */}
                {currentStep.waitForAgentActive && (
                  <Flex
                    align="center"
                    justify="center"
                    gap={2}
                    p={3}
                    bg={agentIsActive ? statusActiveBg : statusWaitingBg}
                    borderRadius="md"
                  >
                    {checkingAgentStatus ? (
                      <>
                        <Spinner size="sm" />
                        <Text fontSize="sm" color={textColor}>
                          Checking agent status...
                        </Text>
                      </>
                    ) : agentIsActive ? (
                      <Text fontSize="sm" fontWeight="bold" color="green.600">
                        ✓ Agent is ready! Click Next to continue.
                      </Text>
                    ) : (
                      <Text fontSize="sm" color={textColorSecondary}>
                        ⏳ Waiting for agent activation...
                      </Text>
                    )}
                  </Flex>
                )}

              {/* Navigation Buttons */}
              <Flex gap={2} pt={4} direction="column">
                <Flex gap={2}>
                  {currentStepIndex > 0 && (
                    <Button
                      variant="outline"
                      size="md"
                      onClick={previousStep}
                      flex={1}
                    >
                      Back
                    </Button>
                  )}
                  
                  {currentStepIndex < totalSteps - 1 ? (
                    <>
                      {/* For steps with autoAdvance, show message but still allow manual skip */}
                      {currentStep.autoAdvance ? (
                        <>
                          <Text
                            fontSize="sm"
                            color={textColorSecondary}
                            textAlign="center"
                            flex={1}
                            alignSelf="center"
                            fontStyle="italic"
                          >
                            {currentStep.id === 'create-agent' 
                              ? 'Complete the form above to continue...'
                              : currentStep.id === 'select-subscription'
                              ? 'Select a plan to continue...'
                              : 'Complete this step to continue...'}
                          </Text>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={nextStep}
                            minW="60px"
                          >
                            Skip
                          </Button>
                        </>
                      ) : (
                        <>
                          {!currentStep.requiresAction && (
                            <Button
                              variant="ghost"
                              size="md"
                              onClick={skipOnboarding}
                              flex={1}
                            >
                              Skip All
                            </Button>
                          )}
                        <Button
                          colorScheme="brand"
                          size="md"
                          onClick={nextStep}
                          flex={2}
                          isDisabled={currentStep.waitForAgentActive && !agentIsActive}
                          isLoading={currentStep.waitForAgentActive && checkingAgentStatus}
                        >
                          {currentStep.id === 'invite-to-meetings' 
                            ? 'Complete Setup'
                            : currentStep.id === 'agent-activation'
                            ? (agentIsActive ? 'Continue' : 'Waiting...')
                            : 'Next'}
                        </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <Button
                      colorScheme="brand"
                      size="md"
                      onClick={nextStep}
                      flex={1}
                      w="100%"
                    >
                      Finish
                    </Button>
                  )}
                </Flex>

                {/* Helper text for steps */}
                {currentStep.autoAdvance && (
                  <Text
                    fontSize="xs"
                    color="brand.500"
                    textAlign="center"
                    fontWeight="medium"
                  >
                    Will auto-advance when complete • or click Skip →
                  </Text>
                )}
                {!currentStep.requiresAction && !currentStep.autoAdvance && currentStepIndex < totalSteps - 1 && (
                  <Text
                    fontSize="xs"
                    color={textColorSecondary}
                    textAlign="center"
                    fontStyle="italic"
                  >
                    This step is optional. Click Next to continue.
                  </Text>
                )}
              </Flex>
              </VStack>
            </Box>
          </Collapse>
        </Card>
      </Box>
    </Slide>
  );
}

